import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
// @ts-ignore
import heroVideo from "../assets/home-video.mp4";

import { ProjectService, ProjectCategory } from "../services/projectService";
import { ProductService, Product } from "../services/productService";

const Home: React.FC = () => {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProjectCategory[]>([]);
  const [categoryImages, setCategoryImages] = useState<Record<number, string>>(
    {}
  );

  const autoplay = useRef(
    Autoplay({ delay: 3000, stopOnInteraction: false })
  );

  // ------------------ Embla carousel ------------------
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      dragFree: false,
      breakpoints: {
        "(max-width: 540px)": {
          align: "center",
          slidesToScroll: 1,
        },
        "(min-width: 541px) and (max-width: 900px)": {
          slidesToScroll: 2,
        },
        "(min-width: 901px)": {
          slidesToScroll: 3,
        },
      },
    },
    [autoplay.current]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [videoSrc, setVideoSrc] = useState<string | null>(null);

  /* ------------------ Fetch featured products ------------------ */
  useEffect(() => {
    ProductService.getRecentProducts()
      .then((products) => setFeaturedProducts(products.slice(0, 8)))
      .catch(console.error);
  }, []);

  /* ------------------ Fetch categories ------------------ */
  useEffect(() => {
    ProjectService.getCategories()
      .then(setCategories)
      .catch(console.error);
  }, []);

  /* -------- Fetch FIRST product image per category -------- */
  useEffect(() => {
    if (!categories.length) return;

    const loadImages = async () => {
      const images: Record<number, string> = {};

      // Limit initial category image fetch to first 6 to reduce network cost
      const catsToLoad = categories.slice(0, 6);

      await Promise.all(
        catsToLoad.map(async (cat) => {
          try {
            const products = await ProductService.getProductsByCategory(
              cat.id.toString()
            );
            const first = products[0];
            const img =
              first?.images?.find((i) => i.isPrimary)?.signedUrl ||
              first?.images?.[0]?.signedUrl;
            if (img) images[cat.id] = img;
          } catch {}
        })
      );

      setCategoryImages(images);
    };

    loadImages();
  }, [categories]);

  /* -------- Defer hero video src until browser idle -------- */
  useEffect(() => {
    const setSrc = () => setVideoSrc(heroVideo);

    // Use requestIdleCallback when available to defer heavy network work
    const handle: any = (window as any).requestIdleCallback
      ? (window as any).requestIdleCallback(setSrc)
      : window.setTimeout(setSrc, 800);

    return () => {
      if ((window as any).cancelIdleCallback && typeof handle === "number") {
        (window as any).cancelIdleCallback(handle);
      } else {
        clearTimeout(handle as number);
      }
    };
  }, []);

  /* ------------------ Product cards ------------------ */
  const productCards = useMemo(
    () =>
      featuredProducts.map((product) => (
        <Link
          to={`/product/${product.id}`}
          key={product.id}
          className="w-[220px] shrink-0 rounded-lg border border-gray-300 bg-white text-center shadow-md no-underline max-[540px]:mx-auto"
        >
          <img
            src={
              product.images?.find((i) => i.isPrimary)?.signedUrl ||
              product.images?.[0]?.signedUrl ||
              "/placeholder.png"
            }
            alt={product.name}
            loading="lazy"
            width={220}
            height={170}
            className="h-[170px] w-full rounded-t-lg object-cover"
            crossOrigin="anonymous"
          />
          <div className="p-2.5">
            <h4 className="my-1 text-base text-black">{product.name}</h4>
            <p className="m-0 text-gray-700">
              {product.description
                ? product.description.split(" ").slice(0, 5).join(" ") + "…"
                : "No description"}
            </p>
          </div>
        </Link>
      )),
    [featuredProducts]
  );

  /* ------------------ Embla dots ------------------ */
  const scrollTo = (index: number) => emblaApi?.scrollTo(index);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  return (
    <>
      {/* ------------------ HERO ------------------ */}
      <section className="relative min-h-[60vh] overflow-y-hidden my-2 mx-7">
        <video
          className="absolute inset-0 z-0 h-full w-full object-cover saturate-95 contrast-95"
          src={videoSrc ?? undefined}
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          poster="/showroom.jpg"
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 z-[1] bg-black/35 pointer-events-none" />

        {/* Hero text */}
        <div className="relative z-[2] flex h-[30vh] flex-col items-start justify-center gap-2 pl-20 text-left text-white pointer-events-none">
          <h2 className="text-white/95 font-normal">
            BEAUTIFUL PRODUCTS, UNFORGETTABLE EXPERIENCE.
          </h2>
          <h1 className="text-[clamp(1.75rem,4.5vw,3.2rem)] leading-[1.05]">
            Artistic Furniture for Homes
          </h1>
          <Link
            to="/shop"
            className="pointer-events-auto inline-block rounded-[10px] bg-[#e8e0a3] px-4 py-2 font-semibold text-black no-underline"
          >
            Shop Now
          </Link>
        </div>

        {/* ------------------ CATEGORIES (EMBLA) ------------------ */}
        <div className="relative w-full py-4 z-20">
          <div
            className="mx-auto overflow-hidden"
            ref={emblaRef}
            role="region"
            aria-label="Categories carousel"
          >
            <div className="flex gap-4 px-2 md:px-4">
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  className="flex-[0_0_auto] w-full max-w-[350px] max-[540px]:w-[90%]"
                >
                  <Link
                    to={`/shop?category=${cat.id}`} 
                    className="block no-underline"
                  >
                    <div className="flex flex-col md:flex-row items-start md:items-center bg-white rounded-lg p-4 shadow-md gap-4">
                      <div className="flex-1 uppercase">
                        <h3>{cat.name}</h3>
                      </div>
                      <div className="md:max-w-none w-full">
                        {categoryImages[cat.id] ? (
                          <img
                            src={categoryImages[cat.id]}
                            alt={cat.name}
                            loading="lazy"
                            className="h-[140px] w-full rounded-lg object-cover"
                            crossOrigin="anonymous"
                          />
                        ) : (
                          <div className="h-[140px] w-full rounded-lg bg-gray-200" />
                        )}
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* Arrows */}
          <button
            className="absolute left-3 top-1/2 z-[5] h-[30px] w-[30px] -translate-y-1/2 rounded-full bg-[#e8e0a3] text-black hidden md:block"
            onClick={() => emblaApi?.scrollPrev()}
          >
            ‹
          </button>
          <button
            className="absolute right-3 top-1/2 z-[5] h-[30px] w-[30px] -translate-y-1/2 rounded-full bg-[#e8e0a3] text-black hidden md:block"
            onClick={() => emblaApi?.scrollNext()}
          >
            ›
          </button>

          {/* Dots */}
          <div className="relative z-[5] mt-4 flex justify-center gap-2">
            {emblaApi &&
              emblaApi.scrollSnapList().map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollTo(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  className={`h-2.5 w-2.5 rounded-full ${
                    index === selectedIndex
                      ? "bg-[#e8e0a3]"
                      : "bg-[#e8e0a3]/60"
                  }`}
                />
              ))}
          </div>
        </div>
      </section>

      {/* ------------------ FEATURED PRODUCTS ------------------ */}
      <section className="mx-auto flex w-[85%] flex-col gap-4 p-4 max-[900px]:w-full">
        <h2 className="uppercase">Featured Products</h2>
        <div className="flex gap-5 overflow-x-auto max-[900px]:grid max-[900px]:grid-cols-[repeat(auto-fill,minmax(200px,1fr))] max-[540px]:grid-cols-1 max-[540px]:justify-center">
          {productCards}
        </div>
      </section>

      {/* ------------------ SHOWROOM/WORKSHOP SECTION ------------------ */}
      <section className="relative mx-8 bg-gray-300">
        <div className="max-w-7xl mx-auto grid gap-6 md:grid-cols-2 items-center">
          
          {/* Left: invitation text */}
          <div className="w-full order-2 md:order-1 px-8">
            <h2 className="text-2xl font-normal mb-3">Workshop & Showroom</h2>
            <p className="text-lg text-gray-700">
              Visit our showroom in Lweza to see some of our products and experience the craftsmanship in person. We invite you to explore designs, materials and finishes up close.
            </p>
            <div className="mt-6">
              {/* <Link
                to="/contact"
                className="inline-block rounded-md bg-[#e8e0a3] px-5 py-2 font-semibold text-black no-underline"
              >
                Visit Showroom
              </Link> */}
            </div>
          </div>

          {/* Right: static showroom image */}
          <div className="w-full order-1 md:order-2">
            <div className="relative overflow-hidden">
              <img
                src="/showroom.jpg"
                alt="Showroom"
                className="w-full h-[300px] object-cover"
              />
            </div>
          </div>
          
        </div>
      </section>

      {/* ------------------ CONTACT/IDEA SECTION ------------------ */}
      <section className="relative m-8">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/textures/texture2.jpg')" }}
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/40" />

        {/* Text content */}
        <div className="relative max-w-7xl mx-auto px-6 py-20 text-center text-white">
          <h2 className="text-2xl font-semibold mb-3">What's your idea?</h2>
          <p className="text-lg">Contact us we shall give it expression.</p>
          <div className="mt-6">
            <Link
              to="/contact"
              className="inline-block outline outline-white rounded-md px-5 py-2 font-semibold text-white no-underline hover:bg-white hover:text-black transition"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

    </>
  );
};

export default Home;
